import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, money } from "@/lib/api";
import StudyCatalogue from "@/components/StudyCatalogue";

function usePublicItems(url, fallback) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const version = useRef(0);
  const load = useCallback(async () => {
    const requestVersion = ++version.current;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(url);
      if (requestVersion !== version.current) return;
      setItems(Array.isArray(data) ? data : data.items || []);
    } catch (failure) {
      if (requestVersion === version.current) setError(applicationErrorMessage(failure, fallback));
    } finally { if (requestVersion === version.current) setLoading(false); }
  }, [url, fallback]);
  const invalidate = useCallback(() => { ++version.current; }, []);
  useEffect(() => { load(); return invalidate; }, [load, invalidate]);
  return { items, loading, error, load };
}

export function Visas() {
  const { items: guides, loading, error, load } = usePublicItems("/visas/guides", "Unable to load visa guides. Please try again.");
  const [country, setCountry] = useState("");
  const [category, setCategory] = useState("");
  const countries = [...new Set(guides.map((g) => g.country))];
  const filtered = guides.filter((g) => (!country || g.country === country) && (!category || g.category === category));

  return (
    <div className="max-w-6xl mx-auto px-5 py-10" data-testid="visas-page">
      <p className="overline">Global visa information centre</p>
      <h1 className="mt-3 text-3xl font-semibold">Visa routes, checklists and official fee guidance</h1>
      <p className="mt-3 text-sm text-slate-600">Use these guides to understand the usual application stages and documents. Rules depend on nationality, destination and personal circumstances. The official authority is always the controlling source.</p>

      <Link className="btn btn-ghost mt-5" to="/visa-support">Track my visa support requests</Link>
      <section className="mt-8" data-testid="visa-guides">
        <div className="flex flex-wrap gap-3">
          <select aria-label="Filter visa guides by country" className="field max-w-xs" value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">All countries</option>{countries.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input aria-label="Filter visa guides by category" className="field max-w-xs" placeholder="Category, e.g. visit" value={category} onChange={(e) => setCategory(e.target.value)} />
        </div>
        {loading && <p role="status" className="mt-5 text-sm">Loading visa guides…</p>}
        {error && <div className="mt-5"><p role="alert" className="text-sm text-red-700">{error}</p><button className="btn btn-ghost mt-3" onClick={load}>Retry loading</button></div>}
        {!loading && !error && filtered.length === 0 && <p className="mt-5 text-sm text-slate-600">No visa guides match these filters.</p>}
        <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {!loading && !error && filtered.map((g) => <article key={g.slug} className="card p-5" data-testid={"visa-guide-" + g.slug}>
            <p className="overline">{g.country} · {g.category}</p>
            <h2 className="mt-2 font-semibold">{g.title}</h2>
            <p className="mt-2 text-sm text-slate-600">{g.summary}</p>
            <h3 className="mt-4 text-sm font-semibold">Typical documents to prepare</h3>
            <ul className="mt-2 list-disc pl-5 text-xs text-slate-600 space-y-1">{g.documents.map((d) => <li key={d}>{d}</li>)}</ul>
            <Link className="btn btn-primary mt-4" to={`/visas/${g.slug}`}>View details and request support</Link>
            <p className="mt-2 text-[11px] text-amber-700">Sources checked {g.last_reviewed}</p>
          </article>)}
        </div>
      </section>

      <div className="card p-6 mt-12" data-testid="calculator-panel">
        <h2 className="text-xl font-semibold">Check current UK visa fees</h2>
        <p className="mt-3 text-sm text-slate-600 max-w-3xl">
          UK visa and sponsorship costs depend on the route, application location,
          duration, dependants and individual circumstances. To avoid displaying an
          inaccurate estimate, please use the official GOV.UK fee information.
        </p>
        <a
          data-testid="official-visa-fees-link"
          className="btn btn-primary mt-5"
          href="https://www.gov.uk/government/publications/visa-regulations-revised-table"
          target="_blank"
          rel="noreferrer"
        >
          Check official UK visa fees
        </a>
        <p className="mt-3 text-xs text-slate-500">
          EuropEdu can provide administrative support but does not guarantee a visa
          outcome or replace advice from an authorised immigration adviser.
        </p>
      </div>
    </div>
  );
}

export function Study() {
  return <StudyCatalogue />;
}

export function Academy() {
  const { user } = useAuth();
  return <AcademyPortal key={JSON.stringify([user?.id, user?.tenant_id])} user={user} />;
}

function AcademyPortal({ user }) {
  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [enrolments, setEnrolments] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pending, setPending] = useState("");
  const actionPending = useRef(false);
  const loadVersion = useRef(0);
  const load = useCallback(async (targetPage = 1) => {
    const version = ++loadVersion.current;
    setLoading(true); setLoadError("");
    try {
      const [courseResponse, enrolmentResponse] = await Promise.all([
        api.get("/courses", { params: { page: targetPage, size: 12 } }), user ? api.get("/enrolments/mine") : Promise.resolve({ data: [] }),
      ]);
      if (version !== loadVersion.current) return;
      setCourses(courseResponse.data.items || []);
      setPage(targetPage); setPages(courseResponse.data.pages || 0); setEnrolments(enrolmentResponse.data || []);
    } catch (error) {
      if (version === loadVersion.current) setLoadError(applicationErrorMessage(error, "Unable to load courses and enrolments. Please try again."));
    } finally { if (version === loadVersion.current) setLoading(false); }
  }, [user]);
  const invalidateLoad = useCallback(() => { ++loadVersion.current; }, []);
  useEffect(() => { load(); return invalidateLoad; }, [load, invalidateLoad]);

  const enrol = async (id) => {
    if (actionPending.current) return;
    if (!user) { setMsg("Please sign in to enrol."); return; }
    actionPending.current = true; setPending("enrol:" + id); setMsg("");
    try {
      const { data } = await api.post("/payments/checkout", {
        item_type: "course", item_id: id, origin_url: window.location.origin,
      });
      if (!data.checkout_url) throw new Error("Checkout could not be opened. Please try again.");
      window.location.href = data.checkout_url;
    } catch (error) { setMsg(applicationErrorMessage(error, "Unable to start enrolment. Please try again.")); }
    finally { actionPending.current = false; setPending(""); }
  };
  const complete = async (enrolment) => {
    if (actionPending.current) return;
    const course = courses.find((item) => item.id === enrolment.course_id);
    const lessons = (course?.modules || []).flatMap((module) => module.lessons || []);
    const completed = enrolment.completed_lessons || [];
    const next = lessons.find((lesson) => !completed.includes(lesson));
    if (!next) { setMsg("All available lessons are already complete."); return; }
    actionPending.current = true; setPending("lesson:" + enrolment.id); setMsg("");
    try {
      await api.post(`/enrolments/${enrolment.id}/complete-lesson?lesson=${encodeURIComponent(next)}`);
      setMsg("Lesson marked complete.");
      await load(page);
    } catch (error) { setMsg(applicationErrorMessage(error, "Unable to complete this lesson. Please try again.")); }
    finally { actionPending.current = false; setPending(""); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10" data-testid="academy-page">
      <p className="overline">Online learning academy</p>
      <h1 className="mt-3 text-3xl font-semibold">Courses</h1>
      {msg && <p role="status" data-testid="academy-message" className="mt-3 text-sm text-slate-700">{msg}</p>}
      {loading && <p role="status" className="mt-6 text-sm">Loading courses and enrolments…</p>}
      {loadError && <div className="mt-6"><p role="alert" className="text-sm text-red-700">{loadError}</p><button className="btn btn-ghost mt-3" onClick={() => load(page)}>Retry loading</button></div>}
      {!loading && !loadError && courses.length === 0 && <p className="mt-6 text-sm">No courses are available yet.</p>}
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {!loading && !loadError && courses.map((c) => {
          const e = enrolments.find((x) => x.course_id === c.id);
          return (
            <article key={c.id} className="card p-5" data-testid={`course-${c.id}`}>
              <h2 className="font-semibold">{c.title}</h2>
              <p className="text-sm text-slate-600">{c.category} · {money(c.fees_pence)}</p>
              <p className="mt-2 text-xs text-slate-500">Provider: {c.provider || "Not supplied"} · Accreditation: {(typeof c.accreditation_status === "string" && c.accreditation_status.trim() ? c.accreditation_status : "not_verified").replace(/_/g, " ")} · Last verified {c.last_verified || "Not supplied"}</p>
              {e ? (
                <>
                  <p className="mt-3 text-sm font-semibold" data-testid={`progress-${c.id}`}>Progress: {e.progress}%</p>
                  <button data-testid={`complete-lesson-${c.id}`} disabled={!!pending || !(c.modules || []).flatMap((m) => m.lessons || []).some((lesson) => !(e.completed_lessons || []).includes(lesson))} onClick={() => complete(e)} className="btn btn-ghost mt-3 w-full justify-center">{pending === "lesson:" + e.id ? "Saving lesson…" : "Complete next lesson"}</button>
                </>
              ) : (
                <button data-testid={`enrol-${c.id}`} disabled={!!pending} onClick={() => enrol(c.id)} className="btn btn-primary mt-4 w-full justify-center">{pending === "enrol:" + c.id ? "Opening checkout…" : <>Enrol — {money(c.fees_pence)}</>}</button>
              )}
            </article>
          );
        })}
      </div>
      {!loadError && pages > 1 && <nav aria-label="Academy course pages" className="mt-6 flex items-center gap-4">
        <button className="btn btn-ghost" disabled={loading || !!pending || page <= 1} onClick={() => load(page - 1)}>Previous</button>
        <span className="text-sm" role="status">Page {page} of {pages}</span>
        <button className="btn btn-ghost" disabled={loading || !!pending || page >= pages} onClick={() => load(page + 1)}>Next</button>
      </nav>}
    </div>
  );
}

export function News() {
  const { items, loading, error, load } = usePublicItems("/news", "Unable to load published news. Please try again.");
  return (
    <div className="max-w-4xl mx-auto px-5 py-10" data-testid="news-page">
      <p className="overline">Newsroom</p>
      <h1 className="mt-3 text-3xl font-semibold">Published articles</h1>
      {loading && <p role="status" className="mt-4 text-sm">Loading published news…</p>}
      {error && <div className="mt-4"><p role="alert" className="text-sm text-red-700">{error}</p><button className="btn btn-ghost mt-3" onClick={load}>Retry loading</button></div>}
      {!loading && !error && items.length === 0 && <p className="mt-4 text-sm text-slate-600">No articles have been approved for publication yet. Drafts are never published without administrator approval.</p>}
      <div className="mt-6 space-y-4">
        {!loading && !error && items.map((a) => (
          <article key={a.id} className="card p-5">
            <h2 className="font-semibold">{a.headline}</h2>
            <p className="text-sm text-slate-600 mt-2">{a.summary}</p>
            <p className="mt-2 text-xs text-slate-500">{a.disclaimer}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

export function Partners() {
  const { user } = useAuth();
  return <PartnerPortal key={JSON.stringify([user?.id, user?.tenant_id])} user={user} />;
}

function PartnerPortal({ user }) {
  const [dash, setDash] = useState(null);
  const [form, setForm] = useState({ entity_type: "individual", legal_name: "", country: "United Kingdom", accepted_agreement: false });
  const [lead, setLead] = useState({ lead_name: "", lead_email: "", service: "verification", order_value_pence: 19900 });
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [pending, setPending] = useState("");
  const actionPending = useRef(false);
  const version = useRef(0);
  const load = useCallback(async () => {
    const requestVersion = ++version.current;
    setLoading(true); setLoadError("");
    try {
      const data = user ? (await api.get("/partners/dashboard")).data : null;
      if (requestVersion === version.current) setDash(data);
    } catch (error) {
      if (requestVersion === version.current) setLoadError(applicationErrorMessage(error, "Unable to load your partner dashboard. Please try again."));
    } finally { if (requestVersion === version.current) setLoading(false); }
  }, [user]);
  const invalidate = useCallback(() => { ++version.current; }, []);
  useEffect(() => { load(); return invalidate; }, [load, invalidate]);

  const apply = async () => {
    if (actionPending.current) return;
    if (!user) { setMsg("Please sign in to apply."); return; }
    if (!form.legal_name.trim() || !form.country.trim() || !form.accepted_agreement) {
      setMsg("Enter your legal name and country, and accept the partner agreement."); return;
    }
    actionPending.current = true; setPending("application"); setMsg("");
    try {
      const { data } = await api.post("/partners/apply", form);
      setDash({ partner: data, leads: [], commission_total_pence: 0 });
      setMsg("Application submitted and awaiting verification by EuropEdu staff.");
    } catch (error) { setMsg(applicationErrorMessage(error, "Unable to submit application. Please try again.")); }
    finally { actionPending.current = false; setPending(""); }
  };
  const registerLead = async () => {
    if (actionPending.current) return;
    const value = Number(lead.order_value_pence);
    if (!lead.lead_name.trim() || !lead.lead_email.trim() || lead.order_value_pence === "" || !Number.isInteger(value) || value < 0) {
      setMsg("Enter the lead name, email and a non-negative whole-number value in pence."); return;
    }
    actionPending.current = true; setPending("lead"); setMsg("");
    try {
      const { data } = await api.post("/partners/leads", { ...lead, order_value_pence: value });
      setDash((current) => ({ ...current, leads: [...(current.leads || []), data], commission_total_pence: (current.commission_total_pence || 0) + (data.commission_pence || 0) }));
      setLead((current) => ({ ...current, lead_name: "", lead_email: "" }));
      setMsg("Lead registered and commission calculated.");
    } catch (error) { setMsg(applicationErrorMessage(error, "Unable to register lead. Please try again.")); }
    finally { actionPending.current = false; setPending(""); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="partners-page">
      <p className="overline">Authorised referral partners</p>
      <h1 className="mt-3 text-3xl font-semibold">EuropEdu Sales Partners</h1>
      <p className="mt-3 text-sm text-slate-600">Partners must not guarantee jobs, sponsorship, admission or visas, or provide unauthorised regulated advice.</p>
      {msg && <p data-testid="partner-message" className="mt-3 text-sm text-slate-800">{msg}</p>}

      {loading && <p role="status" className="mt-6 text-sm">Loading partner dashboard…</p>}
      {loadError && <div className="mt-6"><p role="alert" className="text-sm text-red-700">{loadError}</p><button className="btn btn-ghost mt-3" onClick={load}>Retry loading</button></div>}
      {!loading && !loadError && (!dash?.partner ? (
        <fieldset disabled={!!pending} className="card p-6 mt-8 max-w-xl" data-testid="partner-application-form">
          <label className="block text-sm">Legal name<input data-testid="partner-name-input" className="field mt-1" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} /></label>
          <label className="block text-sm mt-3">Country<input data-testid="partner-country-input" className="field mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></label>
          <label className="flex gap-2 items-start text-sm mt-3">
            <input data-testid="partner-agreement-checkbox" type="checkbox" checked={form.accepted_agreement} onChange={(e) => setForm({ ...form, accepted_agreement: e.target.checked })} />
            I accept the partner agreement and regulatory declarations.
          </label>
          <button data-testid="partner-apply-button" onClick={apply} className="btn btn-primary mt-4 w-full justify-center">{pending === "application" ? "Submitting application…" : "Apply to become a partner"}</button>
        </fieldset>
      ) : (
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <section className="card p-6">
            <h2 className="text-lg font-semibold">Partner status</h2>
            <p className="mt-2 text-sm">{dash.partner.status} · Referral code: {dash.partner.referral_code || "issued after approval"}</p>
            <p className="mt-2 text-sm font-semibold" data-testid="commission-total">Commission total: {money(dash.commission_total_pence)}</p>
          </section>
          <fieldset disabled={!!pending || dash.partner.status !== "approved"} className="card p-6">
            <h2 className="text-lg font-semibold">Register a lead</h2>
            {dash.partner.status !== "approved" && <p className="mt-2 text-sm">Lead registration becomes available after partner approval.</p>}
            <input data-testid="lead-name-input" className="field mt-3" placeholder="Lead name" value={lead.lead_name} onChange={(e) => setLead({ ...lead, lead_name: e.target.value })} />
            <input data-testid="lead-email-input" className="field mt-2" placeholder="Lead email" value={lead.lead_email} onChange={(e) => setLead({ ...lead, lead_email: e.target.value })} />
            <input data-testid="lead-value-input" className="field mt-2" type="number" value={lead.order_value_pence} onChange={(e) => setLead({ ...lead, order_value_pence: e.target.value })} />
            <button data-testid="register-lead-button" onClick={registerLead} className="btn btn-primary mt-3 w-full justify-center">{pending === "lead" ? "Registering lead…" : "Register lead"}</button>
          </fieldset>
          <section className="card p-6 md:col-span-2">
            <h2 className="text-lg font-semibold">Leads</h2>
            <ul className="mt-3 text-sm space-y-1">
              {(dash.leads || []).map((l) => <li key={l.id} className="flex justify-between"><span>{l.lead_name} · {l.service}</span><span>{money(l.commission_pence)} ({l.commission_status})</span></li>)}
            </ul>
          </section>
        </div>
      ))}
    </div>
  );
}

export function WhiteLabel() {
  const features = [
    ["Custom identity", "Use your organisation name, colours, logo and future custom-domain configuration."],
    ["Service modules", "Enable recruitment, sponsorship-job discovery, study, visa information, document workflows and communications."],
    ["Tenant isolation", "Keep staff, candidates, employers, records and audit history separated by organisation."],
    ["Workflow control", "Configure roles, approvals, checklists, referrals, consultations and future modules without rebuilding the platform."],
    ["Operational visibility", "Give authorised teams dashboards for applications, content approvals, source provenance and follow-ups."],
    ["Future-ready integrations", "Connect approved data sources, official messaging APIs and storage when your organisation is ready."],
  ];
  return (
    <div className="max-w-6xl mx-auto px-5 py-10" data-testid="white-label-page">
      <p className="overline">EuropEdu platform</p>
      <h1 className="mt-3 text-3xl font-semibold">A branded recruitment, study and visa-information workspace</h1>
      <p className="mt-4 max-w-3xl text-slate-600">White-label workspaces let an authorised organisation offer its own candidate and employer experience on the EuropEdu platform. This public page explains the service; it is not a candidate, employer or student login page.</p>
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">{features.map(([title, text]) => <article key={title} className="card p-5"><h2 className="font-semibold">{title}</h2><p className="mt-2 text-sm text-slate-600">{text}</p></article>)}</div>
      <section className="card p-6 mt-10">
        <h2 className="text-xl font-semibold">How a branded workspace works</h2>
        <ol className="mt-4 list-decimal pl-5 text-sm text-slate-600 space-y-2">
          <li>Agree the organisation, services, user roles and compliance responsibilities.</li>
          <li>Configure branding, tenant modules, workflows and approved content sources.</li>
          <li>Invite authorised staff and publish the candidate-facing workspace.</li>
          <li>Review data-source approvals, audit logs and service performance continuously.</li>
        </ol>
        <p className="mt-5 text-sm text-slate-600">Interested organisations can <a className="underline text-[#1e3a8a]" href="mailto:info@europedu.co.uk">contact EuropEdu</a> for a workspace assessment. Existing tenant administrators use the internal admin area to manage their configuration.</p>
      </section>
      <p className="mt-6 text-xs text-slate-500">Visa information remains general educational information. Regulated immigration advice, where applicable, must be delivered by an appropriately authorised adviser under a separate engagement.</p>
    </div>
  );
}

